import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { environment } from '../../../../environments/environment';
import { FILING_LOADER_STRATEGY } from '../../../services/mbrs/loaders/filing-loader.token';
import { FILING_LOADER_GRACE_MS } from '../../../utils/loader-timings.util';

/**
 * Full-page blocking overlay shown while the filing-form container
 * hydrates its 24+ sheets on route entry.
 *
 * Displays:
 *  - a spinner
 *  - the current phase label (e.g. "Verifying access...")
 *  - the current sheet being validated with "X of N" progress
 *  - an elapsed-time counter
 *
 * Visibility is gated by a short grace delay
 * ({@link FILING_LOADER_GRACE_MS}) so that cache-hit re-entries which
 * complete in well under that delay do not produce a visible flash.
 *
 * Once a genuinely visible load finishes, a second lightweight popup
 * reports the final elapsed duration with a single "Ok" button. The
 * popup follows the same fast-path suppression rule: if the loading
 * overlay itself never became visible (fast-path / cache-hit), the
 * completion popup is suppressed too — otherwise cached entries would
 * flash a modal every time with no preceding spinner.
 *
 * This is a proof-of-concept for the product team — keep styling
 * lightweight (plain Tailwind) and dependencies minimal.
 */
@Component({
  selector: 'app-filing-load-overlay',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './filing-load-overlay.component.html',
  styleUrls: ['./filing-load-overlay.component.css'],
})
export class FilingLoadOverlayComponent {
  private readonly progressService = inject(FILING_LOADER_STRATEGY);
  private readonly destroyRef = inject(DestroyRef);

  readonly phaseLabel = this.progressService.phaseLabel;
  readonly currentSheetLabel = this.progressService.currentSheetLabel;
  readonly completedCount = this.progressService.completedCount;
  readonly totalCount = this.progressService.totalCount;
  readonly elapsedMs = this.progressService.elapsedMs;

  /** Visibility after the grace-delay gate. */
  readonly visible = signal<boolean>(false);

  /**
   * Whether the loading overlay has actually been shown during the
   * current load cycle. Used to gate the completion popup so fast-path
   * / cache-hit loads (which never tripped the grace timer) do not
   * produce a trailing "Loaded in 00:00.1" modal.
   */
  private readonly wasShown = signal<boolean>(false);

  /** Formatted elapsed time in `MM:SS.d` form for display. */
  readonly elapsedDisplay = computed(() => this.formatElapsed(this.elapsedMs()));

  /** "X of N sheets validated" — hidden while outside the sheet loop. */
  readonly sheetProgressDisplay = computed(() => {
    const total = this.totalCount();
    if (total <= 0) return null;
    return `${this.completedCount()} of ${total} sheets validated`;
  });

  /**
   * Completion popup is shown only when a snapshot duration exists AND
   * the loading overlay was actually visible during the cycle that just
   * ended (fast-path suppression) AND the env-level toggle is on.
   *
   * When `environment.filingLoaderShowCompletionPopup` is false, the
   * popup is suppressed; the companion effect below auto-acknowledges
   * so the strategy's completion snapshot does not linger.
   */
  readonly completionVisible = computed(
    () =>
      environment.filingLoaderShowCompletionPopup &&
      this.progressService.lastCompletedElapsedMs() !== null &&
      this.wasShown(),
  );

  /** Formatted completion-popup elapsed time in `MM:SS.d` form. */
  readonly completedElapsedDisplay = computed(() =>
    this.formatElapsed(this.progressService.lastCompletedElapsedMs() ?? 0),
  );

  private graceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Gate `visible` on the service's isActive signal with a grace delay.
    // Using an effect keeps the component reactive to signal changes
    // without wiring manual subscriptions.
    effect(() => {
      const active = this.progressService.isActive();
      this.clearGraceTimer();

      if (active) {
        this.wasShown.set(false);
        this.graceTimer = setTimeout(() => {
          this.visible.set(true);
          this.wasShown.set(true);
          this.graceTimer = null;
        }, FILING_LOADER_GRACE_MS);
      } else {
        this.visible.set(false);
      }
    });

    // Ensure the timer is cleared if the component is destroyed mid-grace.
    this.destroyRef.onDestroy(() => this.clearGraceTimer());

    // When the env-level toggle is off, the completion popup never renders
    // and `onOk()` is never called — which would leave
    // `lastCompletedElapsedMs` populated until the next `begin()`. Keep
    // the strategy flag-agnostic by auto-acknowledging from the UI layer
    // the moment a snapshot appears under a disabled flag.
    if (!environment.filingLoaderShowCompletionPopup) {
      effect(() => {
        if (this.progressService.lastCompletedElapsedMs() !== null) {
          this.progressService.acknowledgeCompletion();
          this.wasShown.set(false);
        }
      });
    }
  }

  /** Dismiss the completion popup. */
  onOk(): void {
    this.progressService.acknowledgeCompletion();
    this.wasShown.set(false);
  }

  private clearGraceTimer(): void {
    if (this.graceTimer !== null) {
      clearTimeout(this.graceTimer);
      this.graceTimer = null;
    }
  }

  private formatElapsed(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const tenths = Math.floor((ms % 1000) / 100);
    const mm = minutes.toString().padStart(2, '0');
    const ss = seconds.toString().padStart(2, '0');
    return `${mm}:${ss}.${tenths}`;
  }
}
